import { fr } from '../../i18n/locales/fr'
import { dictionaries, useLocale } from '../../i18n/LocaleContext'

type ExpertiseNs = 'adrPage' | 'afscaPage'

export type ExpertiseStep = { readonly title: string; readonly desc: string }
export type ExpertiseFaq = { readonly q: string; readonly a: string }

/**
 * Contenu commun aux deux pages Expertise (tout sauf `anim`, qui diffère par
 * page). Type structurel général (string / readonly string[]) : les littéraux
 * `as const` de `adrPage` ET `afscaPage` y sont assignables.
 */
export type ExpertiseContent = {
  readonly badge: string
  readonly h1Lead: string
  readonly h1Highlight: string
  readonly h1Tail: string
  readonly subhead: string
  readonly ctaPrimary: string
  readonly reassure: string
  readonly hook: {
    readonly eyebrow: string
    readonly title: string
    readonly lead: string
    readonly points: readonly string[]
  }
  readonly scope: {
    readonly eyebrow: string
    readonly title: string
    readonly yesTitle: string
    readonly yes: readonly string[]
    readonly noTitle: string
    readonly no: readonly string[]
    readonly note?: string
  }
  readonly how: {
    readonly eyebrow: string
    readonly title: string
    readonly steps: readonly ExpertiseStep[]
  }
  readonly compliance: {
    readonly eyebrow: string
    readonly title: string
    readonly items: readonly ExpertiseStep[]
  }
  readonly faqEyebrow: string
  readonly faqTitle: string
  readonly faq: readonly ExpertiseFaq[]
}

/**
 * Récupère un sous-arbre i18n COMPLET (objets + tableaux), pas seulement des
 * chaînes — `t()`/`lookup` ne renvoient que des strings.
 *
 * FR est la source de vérité structurelle ; la locale courante surcharge chaque
 * valeur présente et compatible en forme. Robuste tant que EN/NL sont vides
 * (Task 3 → fallback FR) qu'une fois traduits (Task 5 → parité, override total).
 */
function pick<T>(base: T, override: unknown): T {
  if (override === undefined || override === null) return base
  if (Array.isArray(base)) {
    return (
      Array.isArray(override) && override.length === base.length
        ? override.map((o, i) => pick((base as unknown[])[i], o))
        : base
    ) as T
  }
  if (base !== null && typeof base === 'object') {
    if (typeof override !== 'object' || Array.isArray(override)) return base
    const out: Record<string, unknown> = {}
    for (const k of Object.keys(base as Record<string, unknown>)) {
      out[k] = pick(
        (base as Record<string, unknown>)[k],
        (override as Record<string, unknown>)[k],
      )
    }
    return out as T
  }
  // primitive : on garde la traduction si le type correspond, sinon FR.
  return (typeof override === typeof base ? override : base) as T
}

/** Contenu typé d'une page Expertise, résolu pour la langue courante (fallback FR). */
export function useExpertiseContent<K extends ExpertiseNs>(ns: K): (typeof fr)[K] {
  const locale = useLocale()
  const override = (dictionaries[locale] as Record<string, unknown>)[ns]
  return pick(fr[ns], override)
}
